// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import WidgetUI from '../../widgets-ui/demographic-rank';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processDemographicRankWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/demographic-rank';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const DemographicRankWidget = ({
  widget,
  availableMetrics,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.demographicRankT) => {
  const [loading, setLoading] = useSafeState(false);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [widgetDescription, setWidgetDescription] = useSafeState('');
  const [rowsForUI, setRowsForUI] = useSafeState([]);
  const [unitType, setUnitType] = useSafeState('number');
  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible(
    (vi) => uiOptions.disableViewportLoading || vi > 0
  );

  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const title = widget.customTitle || autoTitle;

  const loadWidgetData = useCallback(async () => {
    setLoading(true);
    setError(false);
    reportLoading();

    try {
      const config = {
        metric: widget.metric,
        locations: widget.locations,
        period: widget.period,
        ...(widget.comparisonPeriod && { comparisonPeriod: widget.comparisonPeriod }),
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics: availableMetrics,
      };

      const widgetData = await processDemographicRankWidget(config, deps);

      setAutoTitle(widgetData.title);

      // Extract unitType from columns (column 1 is Male, column 3 is Female)
      const extractedUnitType = widgetData.columns[1]?.defaultUnit || 'number';
      setUnitType(extractedUnitType);

      // Map package output to UI format
      // Package columns: [Age, Male, Male Change, Female, Female Change]
      const rows = widgetData.rows.map((row) => ({
        age: row[0].value, // Age
        male: {
          value: row[1].value, // Male
          changeValue: row[2].value, // Male Change
        },
        female: {
          value: row[3].value, // Female
          changeValue: row[4].value, // Female Change
        },
      }));

      setRowsForUI(rows);

      // Set description if not disabled
      if (!uiOptions.disableDescription) {
        const metric = {
          ...widget.metric,
          details: AvailableMetricsModel.findMetricDetails(
            availableMetrics,
            widget.metric.metricGroupKey,
            widget.metric.metricKey
          ),
        };
        if (metric.details) {
          const demos = Object.values(metric.demographicFilter ?? {}).filter(Boolean).join(" ");
          const desc = AvailableMetricsModel.getMetricDescription(metric.details);
          setWidgetDescription(demos.length ? `${desc} (${demos})` : desc);
        }
      }

      // Report completion to context - use package output directly
      reportComplete(widgetData);
    } catch (err) {
      console.error('Error loading DemographicRank widget:', err);
      setError(true);
      reportError(err.message || 'Failed to load demographic rank widget');
    } finally {
      setLoading(false);
    }
  }, [widget, availableMetrics, uiOptions.disableDescription, reportLoading, reportComplete, reportError]);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;
      loadWidgetData();
    }
  }, [fetchRequired, isVisible, loadWidgetData]);

  const subtitle = buildWidgetDateString(widget);

  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        title={title}
        subtitle={subtitle}
        rows={rowsForUI}
        description={widgetDescription}
        unitType={widget.overrideUnitType ? widget.overrideUnitType : unitType}
      />
    </div>
  );
};

export default DemographicRankWidget;